import { Suspense, lazy, useEffect, useState, type ComponentType } from 'react';
import { startSyncQueue } from './lib/syncQueue';
import { isHidden, setBatterySaver, startPowerGovernor } from './lib/power';
import { markInteractive, startPerfTelemetry } from './lib/perfTelemetry';
import { Navigate, Route, Routes, useLocation } from 'react-router-dom';
import { LOCALES, deliverDue, isCjk } from '@sketchling/core';
import { appNow, useApp, useStoreBilling } from './state/store';
import { fetchComments, refreshConfig, syncAll } from './lib/api';
import { initNativeChrome } from './lib/platform';
import { setMusic } from './lib/sfx';
import { warmVoice } from './lib/tts';
import { addTeacherNotes } from './lib/journal';
import { isLocaleLoaded, loadLocale, translate, useT } from './lib/i18n';
import { loadCjkFont } from './lib/fonts';
import { AppShell } from './components/AppShell';
import { ToastHost, toast } from './components/ui';
import { onImageQuota, persistStorage } from './lib/idb';
import { CelebrationHost, preloadCelebrations } from './components/celebrate/CelebrationHost';
import { Scribble } from './components/characters';
import { ScreenBoundary } from './components/ScreenBoundary';
import { RouteSkeleton } from './components/Skeleton';
// Onboarding / account module
import { Landing } from './screens/onboarding/Landing';
// Lesson module
// Main tabs + features module
import { Learn } from './screens/main/Learn';
// Platform services (push, widget, update gate, managed devices, Ask to Buy, web checkout)
import { PlatformServices } from './screens/system/Gates';
import { BillingReturn, Today } from './screens/system/Routes';
import { SplitTip } from './anim';
import { idlePrefetch, installIntentPrefetch, nextLessonOf } from './lib/content';
import { ContentUpdateNotice } from './components/ContentUpdateNotice';
import { checkForContentUpdate, useContentState } from './lib/contentPack';
import { WowHost } from './components/celebrate/WowHost';
// Monetization P0: wish screen, invite landing, gift purchase / redeem

/* Code-split screens (loaded on first visit; the lesson, Learn and Playground stay in the main bundle). */
const lazyNamed = <K extends string>(load: () => Promise<Record<K, ComponentType<any>>>, name: K) => lazy(() => load().then((m) => ({ default: m[name] })));
/** Purchase / gift / invite pages only where this platform sells (live config `storeBilling`); elsewhere back to the app. */
function BillingOnly({ children }: { children: React.ReactNode }) {
  return useStoreBilling() ? <>{children}</> : <Navigate to="/" replace />;
}

const Paywall = lazyNamed(() => import('./screens/Paywall'), 'Paywall');
const Settings = lazyNamed(() => import('./screens/Settings'), 'Settings');
const Account = lazyNamed(() => import('./screens/Account'), 'Account');
const Profiles = lazyNamed(() => import('./screens/Profiles'), 'Profiles');
const Cheats = lazyNamed(() => import('./screens/Cheats'), 'Cheats');
const DrawTogetherStart = lazyNamed(() => import('./screens/lesson/DrawTogetherStart'), 'DrawTogetherStart');
const PublicShare = lazyNamed(() => import('./screens/lesson/PublicShare'), 'PublicShare');
const Journal = lazyNamed(() => import('./screens/main/Journal'), 'Journal');
const Studio = lazyNamed(() => import('./screens/main/Studio'), 'Studio');
const StudioFlowScreen = lazyNamed(() => import('./screens/features/StudioFlow'), 'StudioFlowScreen');
const ProfileOverview = lazyNamed(() => import('./screens/features/ProfileOverview'), 'ProfileOverview');
const Skills = lazyNamed(() => import('./screens/features/Skills'), 'Skills');
const Challenges = lazyNamed(() => import('./screens/features/Challenges'), 'Challenges');
const Letters = lazyNamed(() => import('./screens/features/Letters'), 'Letters');
const Tales = lazyNamed(() => import('./screens/features/Tales'), 'Tales');
const CollectHub = lazyNamed(() => import('./screens/collect/CollectHub'), 'CollectHub');
const AssetGallery = lazyNamed(() => import('./screens/AssetGallery'), 'AssetGallery');
const StyleBoard = lazyNamed(() => import('./screens/StyleBoard'), 'StyleBoard');
const ArtLab = lazyNamed(() => import('./screens/ArtLab'), 'ArtLab');
const LivingLab = lazyNamed(() => import('./components/living/LivingLab'), 'LivingLab');
const ParentsArea = lazyNamed(() => import('./screens/parents/ParentsArea'), 'ParentsArea');
const FunnelDashboard = lazyNamed(() => import('./screens/measure/FunnelDashboard'), 'FunnelDashboard');
const WishScreen = lazyNamed(() => import('./screens/wish/WishScreen'), 'WishScreen');
const Onboarding = lazyNamed(() => import('./screens/onboarding/Onboarding'), 'Onboarding');
const WhosDrawing = lazyNamed(() => import('./screens/onboarding/WhosDrawing'), 'WhosDrawing');
const UnlockAll = lazyNamed(() => import('./screens/onboarding/UnlockAll'), 'UnlockAll');
const Playground = lazyNamed(() => import('./screens/main/Playground'), 'Playground');
const MiniUnitScreen = lazyNamed(() => import('./screens/lesson/MiniUnitScreen'), 'MiniUnitScreen');
const InviteLanding = lazyNamed(() => import('./screens/invite/InviteLanding'), 'InviteLanding');
const GiftPage = lazyNamed(() => import('./screens/gift/GiftPage'), 'GiftPage');
const GiftRedeem = lazyNamed(() => import('./screens/gift/GiftRedeem'), 'GiftRedeem');
const SignIn = lazyNamed(() => import('./screens/onboarding/SignIn'), 'SignIn');
const Exhibition = lazyNamed(() => import('./screens/main/Exhibition'), 'Exhibition');
const ExhibitionHub = lazyNamed(() => import('./screens/main/Exhibition'), 'ExhibitionHub');
const ObjectHunt = lazyNamed(() => import('./screens/observe/ObjectHunt'), 'ObjectHunt');
const ObjectMuseum = lazyNamed(() => import('./screens/observe/ObjectMuseum'), 'ObjectMuseum');

/** A route behind a live-config switch (off → home). */
function FlagGate({ flag, children }: { flag: 'objectHunt' | 'objectMuseum'; children: React.ReactNode }) {
  const on = useApp((s) => s.config[flag] !== false);
  return on ? <>{children}</> : <Navigate to="/" replace />;
}

/** Where a returning user lands. */
function Home() {
  const done = useApp((s) => s.onboarding.done);
  const profiles = useApp((s) => s.profiles.length);
  const active = useApp((s) => s.activeProfileId);
  const stage = useApp((s) => (s.activeProfileId ? s.progress[s.activeProfileId]?.ftue.stage : undefined));
  if (!done || !profiles) return <Navigate to="/welcome" replace />;
  if (!active) return <Navigate to="/whos-drawing" replace />;
  if (stage === 'chest') return <Navigate to="/unlock-all" replace />;
  return <Navigate to={stage === 'playground' ? '/playground' : '/learn'} replace />;
}

function OrientationGuard() {
  const t = useT();
  return (
    <div className="rotate-guard" aria-hidden>
      <div className="col center">
        <SplitTip size={110} />
        <Scribble mood="curious" size={72} />
        <h2>{t('rotateTitle')}</h2>
        <p className="muted">{t('rotateBody')}</p>
      </div>
    </div>
  );
}

/** Background services: config, sync, letter delivery, family comments. */
function useBackground() {
  const loc = useLocation();
  const music = useApp((s) => s.settings.music);
  const saver = useApp((s) => s.settings.batterySaver);
  useEffect(() => setBatterySaver(saver), [saver]);
  useEffect(() => {
    startPowerGovernor();
    startPerfTelemetry();
    void initNativeChrome();
    void persistStorage();
    onImageQuota(() => toast(translate(useApp.getState().settings.language, 'storageFull')));
    void refreshConfig();
    void checkForContentUpdate();
    void syncAll().then(() => fetchComments());
    void startSyncQueue();
    // Background sync once a minute, only while the app is on screen (no radio / CPU wake-ups in the background).
    const iv = setInterval(() => !isHidden() && void syncAll(), 60_000);
    return () => clearInterval(iv);
  }, []);
  useEffect(() => setMusic(music), [music]);
  // Deliver due teacher letters on navigation (background checker).
  useEffect(() => {
    const st = useApp.getState();
    for (const p of st.profiles) {
      const pr = st.progress[p.id];
      if (!pr?.mailing.pending) continue;
      const { state, delivered } = deliverDue(pr.mailing, appNow());
      if (delivered) {
        st.updateProgress(p.id, (x) => ({ ...x, mailing: state, updatedAt: appNow().toISOString() }));
        addTeacherNotes(p.id, delivered);
      }
    }
  }, [loc.pathname]);
}

/**
 * Native apps read the saved state asynchronously (Capacitor Preferences): until it is loaded, routes would see
 * "no profile" and bounce a cold-start deep link (sketchling://open/lesson/…) to "/". Web hydrates synchronously.
 */
function useHydrated(): boolean {
  const [done, setDone] = useState(() => useApp.persist?.hasHydrated?.() ?? true);
  useEffect(() => {
    if (done) return;
    const off = useApp.persist.onFinishHydration(() => setDone(true));
    if (useApp.persist.hasHydrated()) setDone(true);
    return off;
  }, [done]);
  return done;
}

export function App() {
  useBackground();
  const hydrated = useHydrated();
  const contentRev = useContentState((s) => s.rev);
  const lang = useApp((s) => s.settings.language);
  // Locale: <html lang> + script family (font stacks / CJK line-height in styles/i18n.css), then the lazy
  // dictionaries + content catalog; the first render waits for them so there is no flash of English.
  const [localeReady, setLocaleReady] = useState(() => isLocaleLoaded(lang));
  useEffect(() => {
    const root = document.documentElement;
    root.lang = lang;
    root.dataset.script = LOCALES[lang]?.script ?? 'latin';
    if (isCjk(lang)) void loadCjkFont(lang);
    let alive = true;
    void loadLocale(lang).finally(() => alive && setLocaleReady(true));
    return () => {
      alive = false;
    };
  }, [lang]);
  useEffect(() => {
    if (!hydrated || !localeReady) return;
    markInteractive(window.location.pathname.split('/').slice(0, 2).join('/'));
    // Per-world content: load on intent (pointer-down / focus on a card) + the current world and next lesson when idle.
    installIntentPrefetch();
    const st = useApp.getState();
    const p = st.progress[st.activeProfileId ?? ''];
    const world = p?.currentPath ?? 'intro';
    idlePrefetch(world, nextLessonOf(p, world));
    const warm = setTimeout(preloadCelebrations, 3000);
    return () => clearTimeout(warm);
  }, [hydrated, localeReady]);
  // Voice clips: fetch the locale's manifest once the first screen is interactive (and after a language switch).
  useEffect(() => {
    if (!hydrated || !localeReady) return;
    const id = setTimeout(() => warmVoice(lang), 2500);
    return () => clearTimeout(id);
  }, [hydrated, localeReady, lang]);
  if (!hydrated || !localeReady) return <RouteSkeleton />;
  return (
    <div className="app-root">
      <ScreenBoundary>
      <Suspense fallback={<RouteSkeleton />}>
      <Routes key={`content-${contentRev}`}>
        <Route path="/" element={<Home />} />
        <Route path="/welcome" element={<Landing />} />
        <Route path="/signin" element={<SignIn />} />
        <Route path="/onboarding/:step?" element={<Onboarding />} />
        <Route path="/whos-drawing" element={<WhosDrawing />} />
        <Route path="/unlock-all" element={<UnlockAll />} />
        <Route path="/paywall" element={<Paywall />} />
        <Route path="/parents" element={<ParentsArea />} />
        <Route path="/wish/:kind/:id" element={<WishScreen />} />
        <Route path="/i/:code" element={<BillingOnly><InviteLanding /></BillingOnly>} />
        <Route path="/gift" element={<BillingOnly><GiftPage /></BillingOnly>} />
        <Route path="/gift/redeem" element={<BillingOnly><GiftRedeem /></BillingOnly>} />
        <Route path="/gift/success" element={<BillingOnly><GiftPage outcome="success" /></BillingOnly>} />
        <Route path="/gift/cancel" element={<BillingOnly><GiftPage outcome="cancel" /></BillingOnly>} />
        <Route path="/gift/:code" element={<BillingOnly><GiftRedeem /></BillingOnly>} />
        <Route path="/lesson/:miniUnitId" element={<MiniUnitScreen />} />
        <Route path="/draw-together" element={<DrawTogetherStart />} />
        <Route path="/studio/flow/:flowId" element={<StudioFlowScreen />} />
        <Route path="/s/:shareId" element={<PublicShare />} />
        <Route path="/billing/success" element={<BillingOnly><BillingReturn outcome="success" /></BillingOnly>} />
        <Route path="/billing/cancel" element={<BillingOnly><BillingReturn outcome="cancel" /></BillingOnly>} />
        <Route path="/today" element={<Today />} />
        {/* Developer tools: compiled out of release builds (no hidden features in store builds). */}
        {import.meta.env.DEV && <Route path="/assets" element={<AssetGallery />} />}
        {import.meta.env.DEV && <Route path="/styleboard" element={<StyleBoard />} />}
        {import.meta.env.DEV && <Route path="/artlab" element={<ArtLab />} />}
        {import.meta.env.DEV && <Route path="/livinglab" element={<LivingLab />} />}
        <Route element={<AppShell />}>
          <Route path="/learn" element={<Learn />} />
          <Route path="/playground/:categoryId?" element={<Playground />} />
          <Route path="/journal" element={<Journal />} />
          <Route path="/studio" element={<Studio />} />
          <Route path="/collect/:tab?" element={<CollectHub />} />
          <Route path="/exhibition" element={<ExhibitionHub />} />
          <Route path="/exhibition/:world" element={<Exhibition />} />
          <Route path="/hunt" element={<FlagGate flag="objectHunt"><ObjectHunt /></FlagGate>} />
          <Route path="/museum" element={<FlagGate flag="objectMuseum"><ObjectMuseum /></FlagGate>} />
          <Route path="/sparks" element={<ProfileOverview />} />
          <Route path="/skills/:skillId?" element={<Skills />} />
          <Route path="/challenges" element={<Challenges />} />
          <Route path="/letters" element={<Letters />} />
          <Route path="/tales/:episodeId?" element={<Tales />} />
          <Route path="/settings" element={<Settings />} />
          <Route path="/account" element={<Account />} />
          <Route path="/profiles" element={<Profiles />} />
          {/* Cheats menu + funnel dashboard: dev builds only; testers use the server `unlockAll` flag. */}
          {import.meta.env.DEV && <Route path="/cheats" element={<Cheats />} />}
          {import.meta.env.DEV && <Route path="/cheats/funnel" element={<FunnelDashboard />} />}
        </Route>
        <Route path="*" element={<Navigate to="/" replace />} />
      </Routes>
      </Suspense>
      </ScreenBoundary>
      <CelebrationHost />
      <WowHost />
      <ContentUpdateNotice />
      <PlatformServices />
      <ToastHost />
      <OrientationGuard />
    </div>
  );
}
